/** Dates and times on the booking page are always the business's, not the phone's. */

const cache = new Map<string, Intl.DateTimeFormat>();
function fmt(tz: string, opts: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `${tz}|${JSON.stringify(opts)}`;
  let f = cache.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat("en-US", { timeZone: tz, ...opts });
    cache.set(key, f);
  }
  return f;
}

/** YYYY-MM-DD of an instant in the business's zone. */
export function localDate(iso: string, tz: string): string {
  return fmt(tz, { year: "numeric", month: "2-digit", day: "2-digit" })
    .format(new Date(iso))
    .replace(/^(\d{2})\/(\d{2})\/(\d{4})$/, "$3-$1-$2");
}

export function dayLabel(date: string): string {
  // Noon UTC keeps the calendar date the same in every zone.
  return fmt("UTC", { weekday: "short", month: "short", day: "numeric" }).format(
    new Date(`${date}T12:00:00Z`),
  );
}

export function timeLabel(iso: string, tz: string): string {
  return fmt(tz, { hour: "numeric", minute: "2-digit" }).format(new Date(iso)).replace(":00", "");
}

/** "8 - 10 AM" for an arrival window, "9:30 AM" for an exact time. */
export function slotLabel(s: { windowStart: string; windowEnd: string }, tz: string): string {
  if (s.windowStart === s.windowEnd) return timeLabel(s.windowStart, tz);
  const a = timeLabel(s.windowStart, tz);
  const b = timeLabel(s.windowEnd, tz);
  return a.slice(-2) === b.slice(-2) ? `${a.slice(0, -3)} - ${b}` : `${a} - ${b}`;
}

export function addDays(date: string, n: number): string {
  const d = new Date(`${date}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

export function durationLabel(min: number): string {
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m ? `${h} hr ${m} min` : `${h} hr`;
}

export const US_STATES = [
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "DC",
  "FL",
  "GA",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
] as const;
